import gql from 'graphql-tag'
import { Query } from '@apollo/client/react/components'
import richTextFragmentCut from '@components/renderers/rich-text.fragment-cut'
import { testimonialV2Fragment } from '@components/testimonialv2/testimonialv2.fragment'
import Error from '@components/error'
import LinearProgress from '@material-ui/core/LinearProgress'
import ContentSections from '@components/content-sections'
import PropTypes from 'prop-types'

const cardsFragment = `
  heading
  eyebrow
  image {
    url
  }
  imageUrl
  primaryCtaText
  primaryCtaUrl
  secondaryCtaText
  secondaryCtaUrl
  summary {
    ${richTextFragmentCut}
  }
  cardStyle
`

const CONTENT_SECTION_QUERY = gql`
  query getCustomPageContent($where: CustomPageFilter!, $preview: Boolean)  {
    customPageCollection(limit: 1, preview: $preview, where: $where)  {
      items {
        contentSectionsCollection {
          items {
            __typename
            ... on BasicCta {
              anchorTitle
              heading
              eyebrow
              supportiveCopyRich {
                ${richTextFragmentCut}
              }
              media {
                url
                description
              }
              mediaCaption {
                ${richTextFragmentCut}
              }
              mediaPlacement
              buttonLabel
              buttonLink
              secondaryCtaText
              secondaryCtaUrl
              card {
                heading
                eyebrow
                image {
                  url
                }
                cardVideo {
                  url
                }
                cardVideoAssociatedImage {
                  url
                  description
                }
                summary {
                  ${richTextFragmentCut}
                }
                primaryCtaText
                primaryCtaUrl
                secondaryCtaText
                secondaryCtaUrl
                cardStyle
              }
              theme
            }
            ... on TestimonialV2 {
              anchorTitle
              heading
              ${testimonialV2Fragment}
            }
            ... on Accordion {
              anchorTitle
              heading
              headline
              categories
            }
            ... on SectionCards {
              anchorTitle
              heading
              eyebrow
              summary {
                ${richTextFragmentCut}
              }
              headingAlignment
              theme
              cardsCollection {
                items {
                  ${cardsFragment}
                }
              }
            }
            ... on SectionTabs {
              heading
              eyebrow
              summary {
                ${richTextFragmentCut}
              }
              headingAlignment
              anchorTitle
              tabsCollection {
                items {
                  ${cardsFragment}
                }
              }
            }
          }
        }
      }
    }
  }
`

export default function ContentSectionQuery(props) {
  const {
    slug, route, preview, includeSubNav,
  } = props

  const where = slug
    ? { route, slug }
    : { route, slug_exists: false }

  return (
    <Query query={CONTENT_SECTION_QUERY} variables={{ where, preview }}>
      {({ loading, error, data }) => {
        if (error) {
          return <Error error={error} displayErrorType="page" />
        }
        if (loading) return <LinearProgress color="secondary" />

        const response = data.customPageCollection && data.customPageCollection.items[0]

        return response ? (
          <ContentSections
            sections={response.contentSectionsCollection.items}
            slug={slug}
            includeSubNav={includeSubNav}
          />
        ) : (
          <Error statusCode={404} />
        )
      }}
    </Query>
  )
}

ContentSectionQuery.propTypes = {
  slug: PropTypes.string,
  route: PropTypes.string.isRequired,
  preview: PropTypes.bool,
  includeSubNav: PropTypes.bool,
}
